import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronDown, Filter as FilterIcon, Search, Plus } from 'lucide-react';

/**
 * SegmentedFilterBar — rich, keyword-searchable filter bar.
 *
 * Props:
 *   entityType   "leads" | "contacts" | "companies" | "opportunities" | "projects"
 *   items        full source array of rows
 *   filterDefs   array of { key, label, type ('text'|'multiSelect'|'select'|'tags'), options? }
 *   onFiltered   fn(filteredArray) — called whenever filter result changes
 *
 * Matching semantics (case-insensitive):
 *   - global `search`:  matches any field substring on the row
 *   - text:              substring match on the field
 *   - select / multiSelect: each selected keyword is a SUBSTRING match against the field
 *     (e.g. "project" matches "Project Manager", "VP - Project", "Director of Projects").
 *     A row matches if ANY keyword matches.
 *   - tags: each keyword is a substring match against any tag in the row's tag array.
 *
 * NOTE: Saved segments UI is intentionally hidden (per product decision — coming later).
 */
export default function SegmentedFilterBar({ entityType, items, filterDefs = [], onFiltered }) {
  const [search, setSearch] = useState('');
  const [active, setActive] = useState({}); // { key: array<string> | string }

  const filtered = useMemo(() => {
    let out = items || [];
    if (search.trim()) {
      const s = search.toLowerCase().trim();
      out = out.filter((row) =>
        Object.values(row).some((v) => v != null && String(v).toLowerCase().includes(s))
      );
    }
    Object.entries(active).forEach(([k, val]) => {
      if (val == null || (Array.isArray(val) && val.length === 0) || val === '') return;
      const def = filterDefs.find((d) => d.key === k);
      if (!def) return;
      out = out.filter((row) => {
        const cell = row[k];
        if (def.type === 'multiSelect' || def.type === 'select') {
          const wanted = Array.isArray(val) ? val : [val];
          const cellStr = String(cell ?? '').toLowerCase();
          return wanted.some((w) => cellStr.includes(String(w).toLowerCase()));
        }
        if (def.type === 'text') {
          return cell != null && String(cell).toLowerCase().includes(String(val).toLowerCase());
        }
        if (def.type === 'tags') {
          const cellArr = Array.isArray(cell) ? cell : [];
          const cellStr = cellArr.join(' ').toLowerCase();
          const wanted = Array.isArray(val) ? val : [val];
          return wanted.some((w) => cellStr.includes(String(w).toLowerCase()));
        }
        return true;
      });
    });
    return out;
  }, [items, search, active, filterDefs]);

  // Keep a ref to the latest onFiltered callback so we don't retrigger this
  // effect just because the parent passed an inline arrow function (new ref
  // every render). Without this guard, parents using `onFiltered={(f) => setX(f)}`
  // trigger an infinite render loop ("Maximum update depth exceeded").
  const onFilteredRef = useRef(onFiltered);
  useEffect(() => { onFilteredRef.current = onFiltered; }, [onFiltered]);

  // Some parents pass `items={list.map(...)}` (new array every render). That
  // makes our `filtered` useMemo recompute → new array ref → effect re-fires
  // → setX(new array) on parent → parent re-renders → repeat (infinite loop).
  // Compare by id-list signature so we only notify when the visible set
  // actually changed.
  const lastSigRef = useRef('');
  useEffect(() => {
    const sig = (filtered || []).map((r) => r?.id ?? r?._id ?? JSON.stringify(r)).join('|');
    if (sig === lastSigRef.current) return;
    lastSigRef.current = sig;
    onFilteredRef.current?.(filtered);
  }, [filtered]);

  const updateFilter = (key, val) => setActive((a) => ({ ...a, [key]: val }));
  const clearFilters = () => { setActive({}); setSearch(''); };

  const activeCount = Object.values(active).reduce(
    (a, v) => a + (v && (!Array.isArray(v) || v.length) ? 1 : 0),
    0
  );

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-2.5" data-testid={`filter-bar-${entityType}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${entityType}…`}
            className="w-full border border-slate-300 rounded-sm pl-7 pr-2.5 py-1.5 text-sm focus:border-[#1F7A6E] focus:outline-none"
            data-testid="filter-search"
          />
        </div>

        {(activeCount > 0 || search) && (
          <button
            onClick={clearFilters}
            className="text-[10px] text-slate-500 hover:text-slate-900 font-display font-bold uppercase tracking-wider flex items-center gap-1"
            data-testid="clear-filters-btn"
          >
            <X size={11} /> Clear
          </button>
        )}
      </div>

      {/* Filter fields */}
      <div className="flex items-end gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-400 flex items-center gap-1 mt-1">
          <FilterIcon size={10} /> Filter
        </span>
        {filterDefs.map((def) => (
          <FilterField key={def.key} def={def} value={active[def.key]} onChange={(v) => updateFilter(def.key, v)} />
        ))}
      </div>
    </div>
  );
}

function FilterField({ def, value, onChange }) {
  if (def.type === 'multiSelect' || def.type === 'tags' || def.type === 'select') {
    return <KeywordPickerField def={def} value={value} onChange={onChange} />;
  }
  return (
    <label className="block">
      <span className="sr-only">{def.label}</span>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={def.label}
        className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-xs min-w-[120px]"
        data-testid={`filter-${def.key}`}
      />
    </label>
  );
}

/**
 * KeywordPickerField — searchable combo allowing both option-picking AND
 * custom substring keywords.
 *
 * Behaviour:
 *  - Typing in the search input filters the suggestion list (case-insensitive).
 *  - Pressing Enter or clicking "+ Add" adds the typed value as a custom keyword chip.
 *  - Clicking a suggestion toggles it as a chip (acts as substring match too).
 *  - All chips combine with OR.
 */
function KeywordPickerField({ def, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const opts = (def.options || []).map((opt) => ({
    value: opt.value ?? opt,
    label: opt.label ?? opt,
  }));

  const filteredOpts = opts.filter((opt) => {
    if (!search.trim()) return true;
    return String(opt.label).toLowerCase().includes(search.toLowerCase());
  });

  const toggleChip = (v) => {
    const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
    onChange(next.length ? next : null);
  };

  const addCustomKeyword = () => {
    const k = search.trim();
    if (!k) return;
    if (selected.some((s) => String(s).toLowerCase() === k.toLowerCase())) {
      setSearch('');
      return;
    }
    onChange([...selected, k]);
    setSearch('');
  };

  const onKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomKeyword();
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border rounded-sm font-display font-semibold ${
          selected.length ? 'bg-teal-50 border-[#1F7A6E] text-[#1F7A6E]' : 'bg-white border-slate-300 text-slate-600 hover:border-slate-400'
        }`}
        data-testid={`filter-${def.key}-btn`}
      >
        {def.label}
        {selected.length > 0 && (
          <span className="bg-[#1F7A6E] text-white rounded-full text-[9px] px-1.5 py-0.5">{selected.length}</span>
        )}
        <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute left-0 mt-1.5 w-64 bg-white border border-slate-200 rounded-sm shadow-lg z-30">
          {/* Selected chips */}
          {selected.length > 0 && (
            <div className="px-2 py-2 border-b border-slate-100 flex flex-wrap gap-1">
              {selected.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-teal-50 text-[#1F7A6E] rounded-sm text-[11px] font-semibold"
                  data-testid={`filter-${def.key}-chip-${s}`}
                >
                  {s}
                  <button onClick={() => toggleChip(s)} className="hover:text-red-600" aria-label={`Remove ${s}`}>
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
          {/* Search input + add keyword */}
          <div className="px-2 py-1.5 border-b border-slate-100 flex items-center gap-1">
            <Search size={11} className="text-slate-400 shrink-0" />
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onKey}
              placeholder="Type keyword & Enter…"
              className="flex-1 px-1.5 py-1 text-xs border-0 focus:outline-none"
              data-testid={`filter-${def.key}-search`}
            />
            {search.trim() && (
              <button
                type="button"
                onClick={addCustomKeyword}
                className="shrink-0 p-1 bg-emerald-600 text-white rounded-sm hover:bg-emerald-700"
                title={`Add custom keyword "${search}"`}
                data-testid={`filter-${def.key}-add-kw`}
              >
                <Plus size={11} />
              </button>
            )}
          </div>
          {/* Suggestions list */}
          <div className="max-h-52 overflow-y-auto py-1">
            {filteredOpts.length === 0 && !search.trim() && (
              <div className="px-3 py-2 text-xs text-slate-400 italic">No options available — type a keyword above</div>
            )}
            {filteredOpts.length === 0 && search.trim() && (
              <div className="px-3 py-2 text-xs text-slate-500">
                No match. Press <kbd className="px-1 bg-slate-100 rounded text-[10px]">Enter</kbd> to filter by "{search}".
              </div>
            )}
            {filteredOpts.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 cursor-pointer"
                  data-testid={`filter-${def.key}-opt-${opt.value}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleChip(opt.value)}
                    className="accent-[#1F7A6E]"
                  />
                  <span className={checked ? 'font-semibold' : ''}>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
